import { useEffect, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useTeacherPreview, usePreviewPath } from '@/contexts/TeacherPreviewContext'
import {
  getLessonWithContents,
  getContentsByLesson,
  getStudentContentProgress,
  syncStudentProgress
} from '@/lib/db'
import type { Lesson, Content, StudentContentProgress, ContentStatus } from '@/types'
import {
  ArrowLeft,
  CheckCircle,
  PlayCircle,
  BookOpen,
  ChevronRight,
  Headphones,
  PenTool,
  BookOpenCheck,
  FileText
} from 'lucide-react'
import EditableText from '@/components/edit/EditableText'

const statusColors: Record<ContentStatus, { bg: string; text: string; border: string }> = {
  not_started: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
  in_progress: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' }
}

const statusLabels: Record<ContentStatus, string> = {
  not_started: 'No iniciado',
  in_progress: 'En progreso',
  completed: 'Completado'
}

interface ContentWithProgress extends Content {
  progressData?: StudentContentProgress
}

export default function LessonDetail() {
  const { lessonId } = useParams<{ lessonId: string }>()
  const { student, isInCohort } = useAuth()
  const { isTeacherPreview } = useTeacherPreview()
  const previewPath = usePreviewPath()

  const updateContentTitle = (contentId: string, newTitle: string) => {
    setContents(prev => prev.map(c => c.id === contentId ? { ...c, title: newTitle } : c))
  }

  const isPremium = student?.subscription_status === 'active'
  const hasFullAccess = isPremium && isInCohort
  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [contents, setContents] = useState<ContentWithProgress[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [accessDenied, setAccessDenied] = useState(false)

  useEffect(() => {
    if (lessonId && (student || isTeacherPreview)) {
      loadLessonData()
    }
  }, [lessonId, student, isTeacherPreview])

  const loadLessonData = async () => {
    if (!lessonId) return

    setIsLoading(true)

    // Cargar leccion con contenidos
    const { data: lessonData } = await getLessonWithContents(lessonId)
    if (lessonData) {
      setLesson(lessonData)

      // Premium gate: solo aplica a estudiantes sin acceso completo
      if (!hasFullAccess && !isTeacherPreview) {
        const { data: unitData } = await (await import('@/lib/supabase')).supabase
          .from('lessons')
          .select('order_num, unit:units(order_num)')
          .eq('id', lessonId)
          .single()

        const unitOrder = (unitData as any)?.unit?.order_num
        const lessonOrder = (unitData as any)?.order_num

        if (unitOrder !== 1 || lessonOrder !== 1) {
          setAccessDenied(true)
          setIsLoading(false)
          return
        }
      }
    }

    // Cargar contenidos
    const { data: contentsData } = await getContentsByLesson(lessonId)

    if (contentsData) {
      if (student && !isTeacherPreview) {
        // Cargar progreso de cada contenido (student.id = PK of students table)
        const contentsWithProgress = await Promise.all(
          contentsData.map(async (content) => {
            const { data: progressData } = await getStudentContentProgress(student.id, content.id)
            return {
              ...content,
              progressData: progressData?.[0] || undefined
            }
          })
        )
        setContents(contentsWithProgress)

        // Sync all progress via RPC (handles enum types properly in SQL)
        await syncStudentProgress(student.id)
      } else {
        // Modo previsualización: mostrar contenidos sin progreso
        setContents(contentsData.map(content => ({ ...content, progressData: undefined })))
      }
    }

    setIsLoading(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  if (accessDenied) {
    return <Navigate to={previewPath('/units')} replace />
  }

  if (!lesson) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Lección no encontrada</p>
        <Link to={previewPath('/units')} className="text-blue-600 hover:underline text-sm mt-2 inline-block">
          Volver a unidades
        </Link>
      </div>
    )
  }

  const completedContents = contents.filter(c => c.progressData?.status === 'completed').length

  return (
    <div className="space-y-6" translate="no" lang="fr">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={lesson.unit ? previewPath(`/unit/${lesson.unit_id}`) : previewPath('/units')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          {lesson.unit && (
            <span className="text-sm text-blue-600 font-medium">
              Unidad {(lesson.unit as any).order_num}: {(lesson.unit as any).title}
            </span>
          )}
          <h1 className="text-2xl font-bold text-gray-900">
            Lección {lesson.order_num}:{' '}
            <EditableText
              as="span"
              table="lessons"
              id={lesson.id}
              field="title"
              value={lesson.title}
              placeholder="(sin título)"
              onSaved={(v) => setLesson({ ...lesson, title: v } as Lesson)}
            />
          </h1>
        </div>
      </div>

      {/* Lesson Info */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <EditableText
          as="p"
          table="lessons"
          id={lesson.id}
          field="description"
          value={lesson.description}
          placeholder="(sin descripción)"
          multiline
          className="text-gray-600 mb-4 block"
          onSaved={(v) => setLesson({ ...lesson, description: v } as Lesson)}
        />

        {/* Progress */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Progreso de la lección</span>
            <span className="text-sm font-medium">{completedContents} / {contents.length} contenidos</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${contents.length > 0 ? (completedContents / contents.length) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Skills Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Headphones className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">Escucha</span>
          </div>
          <p className="text-sm text-blue-600">Ejercicios de comprensión auditiva</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <PenTool className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">Escritura</span>
          </div>
          <p className="text-sm text-green-600">Ejercicios de redacción</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <BookOpenCheck className="w-5 h-5 text-orange-600" />
            <span className="font-medium text-orange-800">Lectura</span>
          </div>
          <p className="text-sm text-orange-600">Ejercicios de comprensión lectora</p>
        </div>
      </div>

      {/* Contents */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Contenidos
        </h2>

        <div className="space-y-3">
          {contents.map((content, index) => {
            const status = content.progressData?.status || 'not_started'
            const colors = statusColors[status]

            return (
              <Link
                key={content.id}
                to={previewPath(`/content/${content.id}`)}
                className={`block rounded-xl border-2 overflow-hidden transition-all hover:shadow-md cursor-pointer ${colors.border} ${colors.bg}`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${colors.bg}`}>
                      {status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : status === 'in_progress' ? (
                        <PlayCircle className="w-5 h-5 text-yellow-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-xs text-gray-500">Contenido {index + 1}</span>
                          <EditableText
                            as="h3"
                            table="contents"
                            id={content.id}
                            field="title"
                            value={content.title}
                            placeholder="(sin título)"
                            className="font-medium text-gray-900"
                            onSaved={(v) => updateContentTitle(content.id, v)}
                          />
                        </div>
                        <span className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colors.bg} ${colors.text}`}>
                          {statusLabels[status]}
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 mt-3 bg-blue-600 text-white text-sm rounded-lg">
                        {status === 'completed' ? 'Repasar' : status === 'in_progress' ? 'Continuar' : 'Comenzar'}
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>

                  {/* Progress for in_progress */}
                  {status === 'in_progress' && content.progressData && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                        <span>Ejercicios completados</span>
                        <span className="font-medium">
                          {content.progressData.total_exercises_completed || 0} / {content.progressData.total_exercises || 0}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="flex items-center gap-1">
                          <Headphones className="w-3 h-3 text-blue-500" />
                          <span>{content.progressData.listening_completed || 0} escucha</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <PenTool className="w-3 h-3 text-green-500" />
                          <span>{content.progressData.writing_completed || 0} escritura</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <BookOpenCheck className="w-3 h-3 text-orange-500" />
                          <span>{content.progressData.reading_completed || 0} lectura</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            )
          })}

          {contents.length === 0 && (
            <div className="text-center py-8 bg-white rounded-xl border">
              <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No hay contenidos disponibles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
